// AYA Multiservice — Écran 6 : historique des opérations simulées.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { getOrderHistory, getCatalog, fmtFcfa } from "./multiserviceApi";
import { BrandLogo } from "./BrandLogo";
import "./multiservice.css";

const STATUS_FILTERS = [
  { id: "", label: "Tous" }, { id: "success", label: "Réussies" }, { id: "failed", label: "Échecs" },
  { id: "pending", label: "En attente" }, { id: "processing", label: "En cours" },
];
const STATUS_FR = { success: "RÉUSSIE", failed: "ÉCHEC", pending: "EN ATTENTE", processing: "EN COURS", created: "CRÉÉE", unknown: "INDÉTERMINÉ" };

export default function MultiserviceHistoryPage() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("");
  const [operator, setOperator] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => { getCatalog().then((j) => j.ok && setCatalog(j)).catch(() => {}); }, []);
  const load = useCallback(() => {
    setOrders(null);
    getOrderHistory({ ...(status && { status }), ...(operator && { operator_id: operator }) })
      .then((j) => setOrders(j.ok ? j.orders : []))
      .catch(() => setOrders([]));
  }, [status, operator]);
  useEffect(() => { load(); }, [load]);

  const ops = catalog?.operators || [];
  const dateFr = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="ms-page" data-testid="ms-history-page">
      <header className="ms-header">
        <button className="ms-back" data-testid="ms-history-back-btn" aria-label="Retour" onClick={() => navigate("/multiservice")}><ArrowLeft size={18} /></button>
        <div className="ms-htitles">
          <h1 className="ms-htitle">Historique</h1>
          <p className="ms-hsub">Opérations simulées — données de démonstration</p>
        </div>
        <button className="ms-hbtn" data-testid="ms-history-new-btn" onClick={() => navigate("/multiservice")}><Plus size={14} /> Recharge</button>
      </header>

      <div className="ms-filters" data-testid="ms-history-status-filters">
        {STATUS_FILTERS.map((f) => (
          <button key={f.id} className={`ms-filter ${status === f.id ? "sel" : ""}`} data-testid={`ms-filter-status-${f.id || "all"}`} onClick={() => setStatus(f.id)}>{f.label}</button>
        ))}
      </div>
      <div className="ms-filters" data-testid="ms-history-operator-filters">
        <button className={`ms-filter ${operator === "" ? "sel" : ""}`} data-testid="ms-filter-op-all" onClick={() => setOperator("")}>Tous opérateurs</button>
        {ops.map((o) => (
          <button key={o.id} className={`ms-filter ${operator === o.id ? "sel" : ""}`} data-testid={`ms-filter-op-${o.id}`} onClick={() => setOperator(o.id)}>{o.name}</button>
        ))}
      </div>

      {orders === null ? (
        <p className="ms-empty" data-testid="ms-history-loading">Chargement…</p>
      ) : orders.length === 0 ? (
        <p className="ms-empty" data-testid="ms-history-empty">Aucune opération simulée pour ces filtres.</p>
      ) : (
        orders.map((o) => (
          <div key={o.id}>
            <button className="ms-hist-item" data-testid={`ms-history-item-${o.id}`} onClick={() => setOpenId(openId === o.id ? null : o.id)}>
              <BrandLogo brand={ops.find((x) => x.id === o.operator_id)?.brand} size={40} />
              <span className="ms-hist-body">
                <span className="ms-hist-t">{o.offer_name} <span className={`ms-status ${o.status}`}>{STATUS_FR[o.status] || o.status}</span></span>
                <div className="ms-hist-s">{o.beneficiary_masked} · {fmtFcfa(o.total_amount)} · {o.payment_method_name} · {dateFr(o.created_at)}</div>
              </span>
            </button>
            {openId === o.id && (
              <div className="ms-card" style={{ marginTop: -4 }} data-testid={`ms-history-detail-${o.id}`}>
                <div className="ms-line"><span>Référence</span><b>{o.mock_reference}</b></div>
                <div className="ms-line"><span>Montant du service</span><b>{fmtFcfa(o.service_amount)}</b></div>
                <div className="ms-line"><span>Frais</span><b>{o.fees > 0 ? fmtFcfa(o.fees) : "Gratuit"}</b></div>
                <div className="ms-line"><span>Total simulé</span><b>{fmtFcfa(o.total_amount)}</b></div>
                {o.retry_of && <div className="ms-line"><span>Nouvelle tentative de</span><b>{o.retry_of.slice(0, 8)}…</b></div>}
                {(o.events || []).filter((e) => e.at).map((e) => (
                  <div className="ms-line" key={e.step}><span>{e.label}</span><b>{new Date(e.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</b></div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
      <p className="ms-legal">Mode démonstration : aucune de ces opérations n'a entraîné de mouvement financier réel.</p>
    </div>
  );
}
